/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { colors } from "@farmlands/mui-theme";
import { ButtonBase, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Button } from "../../../../components/Button";
import { Loader } from "../../../../components/Loader";
import MaterialSymbol from "../../../../components/MaterialSymbol";
import Modal from "../../../../components/Modal";
import Steppers from "../../../../components/Stepper/Stepper";
import Typography from "../../../../components/Typography";
import YesNoAction from "../../../../components/YesNoAction";
import { usePermission } from "../../../../hooks/usePermission";
import apiClient from "../../../../services/api-client";
import { selectRemoteConfig } from "../../../../services/firebase/remote-config/remoteConfig.slice";
import TestIds from "../../../../testIds";
import { selectCurrentFarmerAccount } from "../../../azure-b2c/storage";

import { useCampaignContext, withCampaignContextProvider } from "./CampaignContext";
import { QuantityTab } from "./QuantityTab";
import { ReviewTab } from "./ReviewTab";
import SearchAddressTab from "./SearchAddressTab";
import SelectProductTab from "./SelectProductTab";
import { SaleAgreementPromo } from "./types";

interface CampaignModalProps {
  campaignId: string | undefined | null;
  onModalClosed?: () => void;
}

const transformCampaignResponse = (
  data: SaleAgreementPromo,
  hasTfoAccess: boolean
): SaleAgreementPromo => {
  return {
    ...data,
    // hideSite: true,
    editPriceAllowed: data.editPriceAllowed && hasTfoAccess,
  };
};

const CampaignModal = ({ campaignId, onModalClosed }: CampaignModalProps) => {
  const remoteConfig = useSelector(selectRemoteConfig);
  const { state, setState, reset } = useCampaignContext();
  const [open, setOpen] = useState<boolean>(Boolean(campaignId));
  const tabViewBgColor = [1, 2, 3].includes(state.tabIndex) ? "primary25" : undefined;
  const isValidCampaignId = typeof campaignId === "string" && campaignId.length > 0;
  const account = useSelector(selectCurrentFarmerAccount);
  const scrollRef = React.useRef<HTMLElement>(null);
  const canCreateAgreement = usePermission("SalesAgreements_AgreementCreate").allowed;

  const handleModalClose = () => {
    setOpen(false);
    reset();
    if (onModalClosed) {
      onModalClosed();
    }
  };

  useEffect(() => {
    setOpen(Boolean(campaignId));
    setState({ timer: Date.now() });
  }, [campaignId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [state.tabIndex]);

  const saleArgreementPromoQuery = useQuery({
    queryKey: ["sale-agreement-promo", campaignId, account?.accountNumber],
    queryFn: async () => {
      const res = await apiClient.get<SaleAgreementPromo, AxiosRequestConfig>(
        `/sales-agreements/promo/${campaignId}?accountNumber=${account?.accountNumber}`
      );
      return { ...transformCampaignResponse(res.data, Boolean(account?.hasTfoAccess)) };
    },
    enabled: remoteConfig.magnesiumPromoEnabled && isValidCampaignId,
    refetchOnWindowFocus: false,
  });
  const saleAgreementData = saleArgreementPromoQuery.data;

  const magenesiumTypeTitle =
    saleAgreementData?.displayTitle ?? saleAgreementData?.documentTitle ?? "default";

  const agreementNotFound = () => {
    return (
      !saleArgreementPromoQuery.isLoading &&
      !(saleArgreementPromoQuery.isSuccess && saleAgreementData && isValidCampaignId)
    );
  };

  const renderContent = useMemo(() => {
    if (saleArgreementPromoQuery.isFetching) {
      return (
        <Stack direction="column" justifyContent="center" alignItems="center" height="100%">
          <Loader />
        </Stack>
      );
    }

    if (saleArgreementPromoQuery.isSuccess && saleAgreementData && isValidCampaignId) {
      return (
        <>
          {state.tabIndex === 0 ? <SearchAddressTab config={saleAgreementData} /> : null}
          {state.tabIndex === 1 ? (
            <SelectProductTab config={saleAgreementData} campaignId={campaignId} />
          ) : null}
          {state.tabIndex === 2 ? <QuantityTab config={saleAgreementData} /> : null}
          {state.tabIndex === 3 ? (
            <ReviewTab
              config={saleAgreementData}
              refreshCampaign={() => saleArgreementPromoQuery.refetch()}
            />
          ) : null}
        </>
      );
    }

    return null;
  }, [saleArgreementPromoQuery.isFetching, saleArgreementPromoQuery.isSuccess, state.tabIndex]);

  const setDefaultSite = (saleAgreementData: SaleAgreementPromo) => {
    if (saleAgreementData) {
      const { products, defaultSiteId, deliveryOptions } = saleAgreementData;
      const defaultSite = products.find((s) => s.siteId === defaultSiteId);

      // set the default site
      if (defaultSite && defaultSiteId) {
        setState({
          siteSelected: {
            siteId: defaultSite.siteId,
            siteName: defaultSite.siteName,
            sitePostcode: defaultSite.sitePostcode,
          },
        });
      }

      // set the default delivery options
      if (deliveryOptions.length === 1) {
        setState({
          deliveryOption: deliveryOptions[0],
        });
      } else {
        const prefferedDeliveryOption = deliveryOptions.find((d) => d.isPreferred);
        setState({
          deliveryOption: prefferedDeliveryOption,
        });
      }
    }
  };
  React.useEffect(() => {
    if (saleAgreementData) {
      setDefaultSite(saleAgreementData);
    }
  }, [saleAgreementData, state.timer]);

  const [keyboardPadding, setKeyboardPadding] = useState(0);

  React.useEffect(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios") {
      Keyboard.addListener("keyboardDidShow", (info) => {
        setKeyboardPadding(info.keyboardHeight / 10);
      });

      Keyboard.addListener("keyboardWillHide", () => {
        setKeyboardPadding(0);
      });
      return () => void Keyboard.removeAllListeners();
    }
  }, []);

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  if (remoteConfig.magnesiumPromoEnabled === false) {
    return null;
  }

  if (agreementNotFound() || !canCreateAgreement) {
    return <CampaignNotFound handleModalClose={handleModalClose} open={open} isSm={isSm} />;
  }

  return (
    <Modal
      testId={TestIds.feat.agreement.campaignModal.container}
      sx={{
        overflow: "hidden",
      }}
      title={
        state.tabIndex > 0 ? (
          <ButtonBase
            onClick={() => setState({ tabIndex: state.tabIndex - 1 })}
            sx={{ color: colors.grey700 }}
          >
            <Stack display="inline-flex" flexDirection="row" gap="6px">
              <MaterialSymbol>west</MaterialSymbol>
              <Typography variant="body2">Back</Typography>
            </Stack>
          </ButtonBase>
        ) : null
      }
      open={open}
      scrollRef={scrollRef}
      handleClose={handleModalClose}
      header={
        saleArgreementPromoQuery.isSuccess ? (
          <Steppers
            disableStepperClick
            enableBackBtn={false}
            maxSteps={4}
            activeStep={state.tabIndex}
            title={magenesiumTypeTitle}
            previousStep={1}
            setPageIndex={(value) => {
              setState({ tabIndex: value });
            }}
          />
        ) : null
      }
      contentBg={tabViewBgColor}
      headerDivider
      maxWidth="md"
      paperProps={{
        sx: {
          height: {
            sm: "inherit",
            md: "844px",
          },
          overflow: "hidden",
        },
      }}
      dialogContentSx={{ paddingBottom: `${keyboardPadding}px` }}
    >
      {renderContent}
    </Modal>
  );
};

type CampaignNotFoundProps = {
  handleModalClose: () => void;
  open: boolean;
  isSm: boolean;
};

const CampaignNotFound: React.FC<CampaignNotFoundProps> = ({ handleModalClose, open, isSm }) => {
  return (
    <Modal
      testId={TestIds.feat.agreement.campaignModal.notFound}
      handleClose={handleModalClose}
      title="Agreement not found"
      open={open}
      footer={
        isSm && (
          <Button sx={{ width: "100%" }} variant="contained" onClick={handleModalClose}>
            Close
          </Button>
        )
      }
    >
      <YesNoAction body="Sorry, this agreement type is no longer available." />
    </Modal>
  );
};

export default withCampaignContextProvider<CampaignModalProps>(CampaignModal);
